import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Lock, CreditCard, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtEur } from "@/components/admin/ui-lite";
import { fmtParisLong } from "@/components/location/DateTimeFields";

export default function PayAcompte() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const load = () => api.get(`/billing/pay/${token}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) { load(); return; }
    setPolling(true);
    let n = 0;
    const tick = () => api.get(`/billing/pay/${token}/status/${sid}`).then((r) => {
      if (r.data.payment_status === "paid" || ++n > 8) { setPolling(false); load(); } else setTimeout(tick, 2000);
    }).catch(() => { setPolling(false); load(); });
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sp]);
  const go = async () => {
    setBusy(true);
    try { const r = await api.post(`/billing/pay/${token}/checkout`, { origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  if (!d || polling) return <div className="pt-40 pb-24 text-center text-white/45 italic" data-testid="acompte-loading">{polling ? "Confirmation du paiement…" : "Chargement…"}</div>;
  return (
    <section className="pt-32 md:pt-40 pb-24 px-6 max-w-2xl mx-auto" data-testid="acompte-page">
      <p className="eyebrow">Acompte · devis {d.number}</p>
      <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Confirmez votre <span className="italic gold-text">réservation.</span></h1>
      <div className="card-luxe p-6 mt-8 space-y-2 text-sm">
        <p className="text-white/70">{d.client_name}</p>
        {d.rental_start_at && <p className="text-white/70 first-letter:uppercase" data-testid="acompte-dates">Départ du matériel : {fmtParisLong(d.rental_start_at)}<br />Retour : {fmtParisLong(d.rental_end_at)}</p>}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#DCB457]/15 mt-4">
          <div><p className="text-[10px] uppercase tracking-[0.2em] text-white/45">Total</p><p className="font-serif-luxe text-2xl">{fmtEur(d.total_ttc)}</p></div>
          <div><p className="text-[10px] uppercase tracking-[0.2em] text-white/45">Acompte</p><p className="font-serif-luxe text-2xl gold-text" data-testid="acompte-amount">{fmtEur(d.deposit_ttc)}</p></div>
        </div>
      </div>
      {d.acompte_paid ? (
        <div className="card-luxe p-6 mt-6 border-emerald-500/30 space-y-3" data-testid="acompte-paid">
          <p className="flex items-center gap-2 text-emerald-300"><CheckCircle2 size={18} />Acompte reçu — merci ! Facture {d.acompte_invoice_number} envoyée par email.</p>
          {d.caution_url && <p className="text-sm text-white/70">Dernière étape : enregistrer votre carte pour le dépôt de garantie (rien n'est débité à cette étape).</p>}
          {d.caution_url && <Link to={d.caution_url} className="btn-gold inline-flex gap-2" data-testid="acompte-go-caution"><ShieldCheck size={14} />Finaliser mon dépôt de garantie</Link>}
        </div>
      ) : !d.is_signed ? (
        <p className="mt-6 text-sm text-amber-200" data-testid="acompte-not-signed">Le devis doit d'abord être signé.</p>
      ) : (
        <div className="mt-6 space-y-4">
          <button onClick={go} disabled={busy} className="btn-gold w-full sm:w-auto inline-flex justify-center gap-2 disabled:opacity-50" data-testid="acompte-pay-btn"><CreditCard size={15} />{busy ? "Redirection…" : `Payer l'acompte de ${fmtEur(d.deposit_ttc)}`}</button>
          <p className="text-xs text-white/50 flex items-center gap-2"><Lock size={12} />Paiement sécurisé par Stripe · My Animation ne conserve aucune donnée bancaire.</p>
        </div>
      )}
    </section>
  );
}
